import type { ReactNode } from 'react';
import {
  Controller,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';

import styles from './form-field.module.scss';

type RenderArgs<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = Parameters<ControllerProps<TFieldValues, TName>['render']>[0];

export type FormFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = {
  name: TName;
  control: ControllerProps<TFieldValues, TName>['control'];
  /** Plain string, or a node (e.g. a label with a trailing info-tooltip icon). */
  label?: ReactNode;
  help?: string;
  required?: boolean;
  children: (args: RenderArgs<TFieldValues, TName>) => ReactNode;
};

export function FormField<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  name,
  control,
  label,
  help,
  required,
  children,
}: FormFieldProps<TFieldValues, TName>) {
  return (
    <Controller
      name={name}
      control={control}
      render={(args) => (
        <div className={styles.field}>
          {label ? (
            <label
              className={[styles.label, required ? styles.requiredLabel : null]
                .filter(Boolean)
                .join(' ')}
              htmlFor={name}
            >
              {label}
            </label>
          ) : null}
          {children(args)}
          {help ? <div className={styles.help}>{help}</div> : null}
          {args.fieldState.error?.message ? (
            <div className={styles.error} role="alert">
              {args.fieldState.error.message}
            </div>
          ) : null}
        </div>
      )}
    />
  );
}
